import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Layout } from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useAuthStore } from '../store/authStore';

/**
 * Whether the signed-in user may use an admin page: 'yes', 'no', or
 * 'checking' while the server's admin check is still out.
 *
 * Admin pages used to read user.is_admin alone, and sign-in once returned the
 * user without it, so an admin was refused until a reload. Now either the
 * user record or the server's check (checkAdminStatus) is enough, and nobody
 * is refused before the check has answered.
 */
export function useAdminAccess() {
  const { user, isAdmin, adminStatusChecked, checkAdminStatus } = useAuthStore();
  const yes = !!user?.is_admin || isAdmin;

  useEffect(() => {
    if (!yes && !adminStatusChecked) checkAdminStatus();
  }, [yes, adminStatusChecked, checkAdminStatus]);

  if (yes) return 'yes';
  return adminStatusChecked ? 'no' : 'checking';
}

/** What an admin page shows instead of itself, until access is 'yes'. */
export function AdminGate({ access }) {
  return (
    <Layout>
      {access === 'checking' ? (
        <div className="flex justify-center py-12" data-testid="admin-gate-checking">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="container mx-auto" data-testid="admin-gate-denied">
          <Card>
            <CardHeader>
              <CardTitle>Access denied</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">You don&apos;t have permission to access this page.</p>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}
